import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';
import { useSession } from '@/lib/auth/session';
import { type PersonnelCareer } from '@/lib/types';

/**
 * 콘도 · 복지몰 계정 · 경력기술 신고 (V321 · 사용자 지시 2026-09-22).
 *
 * <p>`useWelfare` 와 나눈 이유는 <b>부르는 때가 다르기</b> 때문이다. 저쪽은 복지 탭을 열면
 * 한 번에 오는 묶음이고, 이 셋은 각자의 탭을 눌렀을 때만 필요하다 — 콘도를 안 여는 사람에게
 * 콘도 목록을 실어 보내지 않는다.
 */

export type Condo = {
  id: string;
  name: string;
  location: string | null;
  guide: string | null;
  linkUrl: string | null;
  contact: string | null;
};

/**
 * 내 복지몰 계정.
 *
 * <b>비밀번호는 오지 않는다</b> — 있는지 여부만 온다. 값은 「보기」를 눌러 본인 확인을 통과했을
 * 때만 그 자리에서 받는다(라이선스 키와 같은 선).
 */
export type MyMallAccount = {
  loginId: string | null;
  hasPassword: boolean;
  /** 나에게만 해당하는 말. 인사팀이 그 줄에 적었을 때만 있다. */
  note: string | null;
  /** 전사 공통 사용안내. 계정이 없어도 온다 — 회사 전체의 말이기 때문이다. */
  guide: string | null;
};

export type CareerStatement = {
  id: string;
  content: string;
  periodFrom: string | null;
  periodTo: string | null;
  evidenceName: string | null;
  evidenceUrl: string | null;
  /** PENDING(확인 중) · APPROVED(확정) · REJECTED(반려) */
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  reviewNote: string | null;
  reviewerName: string | null;
  reviewedAt: string | null;
  createdAt: string;
};

function useMine<T>(key: string, path: string, enabled = true): UseQueryResult<T> {
  const { data: session } = useSession();
  const userId = session?.user?.id ?? null;

  return useQuery({
    queryKey: [key, userId],
    queryFn: () => apiFetch<T>(path),
    enabled: !!userId && enabled,
    staleTime: 60_000,
  });
}

export function useCondos() {
  return useMine<Condo[]>('mypage-condos', '/v1/mypage/condos');
}

export function useMyMallAccount() {
  return useMine<MyMallAccount>('mypage-mall-account', '/v1/mypage/welfare-mall');
}

/**
 * 복지몰 비밀번호 보기. 돌려받은 값은 <b>캐시에 담지 않는다</b> — 화면을 떠나면 사라져야 한다.
 */
export function useRevealMallPassword() {
  return useMutation({
    mutationFn: (password: string) =>
      apiFetch<{ password: string }>('/v1/mypage/welfare-mall/password', {
        method: 'POST',
        body: { password },
      }),
  });
}

/**
 * 내 경력 — <b>확정된 것</b>. 인사관리의 같은 목록과 경로가 다르다(`GET /v1/mypage/careers`):
 * 저쪽은 남의 경력을 훑는 화면이라 `hr_management` 를 요구하고, 자기 것을 보는 데 그 권한을
 * 요구하면 대부분의 직원에게 「인사 › 경력」 이 통째로 403 이 된다.
 */
export function useMyCareers() {
  return useMine<PersonnelCareer[]>('mypage-careers', '/v1/mypage/careers');
}

export function useMyCareerStatements() {
  return useMine<CareerStatement[]>('mypage-career-statements', '/v1/mypage/career-statements');
}

/**
 * 경력기술증명서를 올린다. 두 걸음이다 — presigned PUT URL 을 받아 스토리지에 직접 올리고
 * 키만 돌려준다. 파일 바이트가 API 를 지나지 않는다(프로필 사진과 같은 규칙).
 */
export async function uploadCareerEvidence(file: File): Promise<string> {
  const { key, url } = await apiFetch<{ key: string; url: string }>('/v1/mypage/career-statements/uploads', {
    method: 'POST',
    body: { fileName: file.name },
  });

  const put = await fetch(url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
  });
  if (!put.ok) {
    throw new Error('증명서를 올리지 못했습니다.');
  }
  return key;
}

export type CareerStatementWrite = {
  content: string;
  periodFrom: string | null;
  periodTo: string | null;
  evidenceKey: string;
  evidenceName: string | null;
};

export function useSubmitCareerStatement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CareerStatementWrite) =>
      apiFetch<CareerStatement>('/v1/mypage/career-statements', { method: 'POST', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['mypage-career-statements'] }),
  });
}

/** 확인 중인 것만 내린다. 판정이 끝난 줄은 이력이라 서버가 거절한다. */
export function useWithdrawCareerStatement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/v1/mypage/career-statements/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['mypage-career-statements'] }),
  });
}
